import React from 'react';
import { Box, Optional } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import UniCard from '../UniCard';
import themeStyles from '../UniCard/styles/Card.styles';
import useCalculatorParams from './hooks/useCalculatorParams';
import useHoverState from './useHoverState';
import { EqualHeightElement } from '../../utils/components/EqualHeight';
import { LOYALTY_URL_PARAM } from '../../utils/constants';
import { PaymentMethod, selectPrimaryCalculatorIsPaymentMethodCash } from '../../core/reducers/financialCalculators';
import { filterUndefinedAndEmptyValues, gtmEvent } from '../../utils/gtmEvents';
import { removeAllTagsFromMarkdown } from '../../utils/markdown';
import { selectCanUseLoyalty } from '../../core/reducers/offerOptions';
import { useAppSelector } from '../../core/utils';
const UniCardWrapper = themeStyles.apply(Box, 'UniCardWrapper');
const ModelCardUI = ({ model, hasShadow, inventoryStatus, inventoryStatusPosition, includeCalculatorParams, nextStepUrl, hasIntegratedFilters, }) => {
    const { t } = useTranslation();
    const { href, linktype } = nextStepUrl;
    const { modelGtmName, modelName, modelKey, trimKey, year, driveRange, primaryThumbnail, secondaryThumbnail, filters, paymentData, availablePaymentMethods, } = model;
    const calculatorParams = useCalculatorParams(modelKey, year, paymentData === null || paymentData === void 0 ? void 0 : paymentData.data);
    const currentPaymentMethodCash = useAppSelector(selectPrimaryCalculatorIsPaymentMethodCash);
    const isPaymentMethodCash = (currentPaymentMethodCash && availablePaymentMethods.includes(PaymentMethod.cash)) ||
        (availablePaymentMethods.length === 1 && availablePaymentMethods[0] === PaymentMethod.cash);
    const name = modelName.toUpperCase();
    const { thumbnail, backgroundColor, onMouseEnter, onMouseLeave } = useHoverState({
        primaryThumbnail,
        secondaryThumbnail,
    });
    const canUseLoyalty = useAppSelector(selectCanUseLoyalty);
    const cardWrapperVariant = !hasShadow ? 'hasShadow' : 'noShadow';
    const cardContainerMinHeight = hasIntegratedFilters ? ['511px', '527px', '527px'] : '0';
    const hasSoldout = inventoryStatus === 'soldOut';
    const paymentTooltip = {
        year: year || '',
        model: name || '',
        trim: model.trimName || '',
    };
    const gtmPushHandler = () => {
        var _a;
        const gtmPayload = hasIntegratedFilters
            ? {
                event: 'select_model',
                model: removeAllTagsFromMarkdown(modelGtmName),
                body_style: removeAllTagsFromMarkdown((_a = filters === null || filters === void 0 ? void 0 : filters.bodyType) === null || _a === void 0 ? void 0 : _a[0]),
                model_year: year,
            }
            : {
                event: 'dealer_inventory_model_select',
                model_year: year,
                model: modelKey,
            };
        gtmEvent(filterUndefinedAndEmptyValues(gtmPayload));
    };
    const ctaParams = includeCalculatorParams
        ? {
            modelKey,
            modelYear: year,
            ...calculatorParams,
        }
        : {
            modelKey,
            modelYear: year,
        };
    const cta = {
        gtmEvent: () => gtmPushHandler(),
        text: `${t('Pages.Models.Exploration.selectModelButton')}`,
        href,
        linktype,
        target: '',
        label: `${year} ${name}`,
        params: {
            ...ctaParams,
            ...(canUseLoyalty ? { [LOYALTY_URL_PARAM]: 'true' } : {}),
        },
    };
    const cardEvents = {
        onMouseEnter,
        onMouseLeave,
    };
    const showStatusBadge = inventoryStatus && !hasSoldout;
    return (React.createElement(UniCardWrapper, { variant: cardWrapperVariant, "data-testid": "cy-uni-card", minHeight: cardContainerMinHeight },
        React.createElement(UniCard, { componentName: "ModelCardGrid", events: cardEvents, onHoverBackgroundColor: backgroundColor, cardContainerMinHeight: cardContainerMinHeight },
            React.createElement(UniCard.TitleWrapper, null,
                React.createElement(UniCard.Title, { title: `${year} ${name}`, styles: {
                        minHeight: '56px',
                    }, cta: cta })),
            React.createElement(EqualHeightElement, { name: "model-card-pricing", placeholder: !hasIntegratedFilters },
                React.createElement(Optional, { when: hasIntegratedFilters }, model.paymentData ? React.createElement(UniCard.Pricing, { ...model.paymentData }) : null)),
            React.createElement(EqualHeightElement, { name: "model-card-status-badge", placeholder: inventoryStatusPosition === 'top' && !showStatusBadge },
                React.createElement(Optional, { when: inventoryStatusPosition === 'top' && showStatusBadge },
                    React.createElement(UniCard.StatusBadge, { status: inventoryStatus, badgeAlignment: "center" }))),
            React.createElement(EqualHeightElement, { name: "model-card-image" },
                React.createElement(UniCard.Image, { src: thumbnail === null || thumbnail === void 0 ? void 0 : thumbnail.src, alt: thumbnail === null || thumbnail === void 0 ? void 0 : thumbnail.alt, cta: cta })),
            React.createElement(Optional, { when: hasIntegratedFilters },
                React.createElement(EqualHeightElement, { name: "model-card-drive-range", placeholder: !driveRange },
                    React.createElement(UniCard.DriveRange, { driveRange: driveRange && `${t('Shared.Common.driveRangeLabel', { driveRange })}`, driveRangeTooltip: `${t('Shared.Common.driveRangeTooltipText')}` })),
                React.createElement(EqualHeightElement, { name: "model-card-payment", placeholder: !trimKey },
                    React.createElement(Optional, { when: trimKey && !isPaymentMethodCash },
                        React.createElement(UniCard.PaymentDetails, { payment: model.paymentData, paymentTooltip: paymentTooltip })))),
            React.createElement(Box, { mt: "auto" },
                React.createElement(EqualHeightElement, { name: "model-card-status-badge", placeholder: inventoryStatusPosition === 'bottom' && !showStatusBadge },
                    React.createElement(Optional, { when: inventoryStatusPosition === 'bottom' && showStatusBadge },
                        React.createElement(Box, { mb: "s" },
                            React.createElement(UniCard.StatusBadge, { status: inventoryStatus, badgeAlignment: "left" })))),
                React.createElement(UniCard.CTA, { styles: { width: '100%' }, cta: cta })))));
};
export default ModelCardUI;
